import { ForwardedRef, RefObject, useImperativeHandle } from 'react';

import { PlayerControlsRef } from './components/Controls';
import { PlayerControlTypes } from './components/Controls/PlayerControlTypes/types';
import { FullscreenWrapperRef } from './components/FullscreenWrapper';
import { PlayerVideoRef } from './components/PlayerVideo/PlayerVideo.types';
import { TogglePlayFunction } from './types';

export interface KitPlayerRef {
  play(): void;
  pause(): void;
  togglePlay(): void;
  toggleControls: PlayerControlsRef['toggleControls'];
  skipForward: PlayerControlsRef['skipForward'];
  skipBackward: PlayerControlsRef['skipBackward'];
  fastSeekForward: PlayerControlsRef['fastSeekForward'];
  fastSeekBackward: PlayerControlsRef['fastSeekBackward'];
  stopFastSeeking: PlayerControlsRef['stopFastSeeking'];
  incrementFastSeeking: PlayerControlsRef['incrementFastSeeking'];
  toggleFullscreen(): void;
  enterFullscreen(): void;
  exitFullscreen(): void;
  videoSeekTo(seconds: number): void;
  getPlayerState():
    | {
        areControlsVisible: boolean;
        focusedControl: PlayerControlTypes | undefined;
        currentTime: number;
        isPlaying: boolean;
        isFastSeeking: boolean;
        fastSeekingDirection: 'forward' | 'backward';
        isFullscreen: boolean;
        totalDuration: number;
        isPrePlayPosterVisible: boolean;
      }
    | undefined;
}

export function useSetupKitPlayerRefHandle(
  kitPlayerRef: ForwardedRef<KitPlayerRef>,
  {
    playerVideoRef,
    playerControlsRef,
    fullscreenRef,
    togglePlay,
    isPlaying,
    totalDurationSeconds,
    isPrePlayPosterVisible,
  }: {
    playerVideoRef: RefObject<PlayerVideoRef | null>;
    playerControlsRef: RefObject<PlayerControlsRef | null>;
    fullscreenRef: RefObject<FullscreenWrapperRef | null>;
    togglePlay: TogglePlayFunction;
    isPlaying: boolean;
    totalDurationSeconds: number;
    isPrePlayPosterVisible: boolean;
  }
): void {
  /**
   * @TODO refactor each of the below APIs to use playerControlsRef instead.
   *
   * Note: this will help consolidate business and analytics logic in one place
   */
  useImperativeHandle(
    kitPlayerRef,
    () => ({
      play: () => togglePlay({ forceTo: true }),
      pause: () => togglePlay({ forceTo: false }),
      togglePlay: () => togglePlay(),
      toggleControls(options) {
        playerControlsRef.current?.toggleControls(options);
      },
      fastSeekForward() {
        playerControlsRef.current?.fastSeekForward();
      },
      fastSeekBackward() {
        playerControlsRef.current?.fastSeekBackward();
      },
      stopFastSeeking() {
        playerControlsRef.current?.stopFastSeeking();
      },
      incrementFastSeeking() {
        playerControlsRef.current?.incrementFastSeeking();
      },
      skipForward() {
        playerControlsRef.current?.skipForward();
      },
      skipBackward() {
        playerControlsRef.current?.skipBackward();
      },
      toggleFullscreen() {
        fullscreenRef.current?.toggleFullscreen();
      },
      enterFullscreen() {
        fullscreenRef.current?.enterFullscreen();
      },
      exitFullscreen() {
        fullscreenRef.current?.exitFullscreen();
      },
      videoSeekTo(seekToTime) {
        playerVideoRef.current?.seekTo(seekToTime);
      },
      getPlayerState() {
        if (playerControlsRef.current && fullscreenRef.current) {
          return {
            areControlsVisible:
              playerControlsRef.current.getAreControlsVisible(),
            focusedControl: playerControlsRef.current.getFocusedControl(),
            currentTime:
              playerVideoRef?.current?.getCurrentTimeInSeconds() ?? -1,
            isPlaying: isPlaying,
            isFastSeeking: playerControlsRef.current.getIsFastSeeking(),
            fastSeekingDirection:
              playerControlsRef.current.getFastSeekingDirection(),
            isFullscreen: fullscreenRef.current.getIsFullscreen(),
            totalDuration: totalDurationSeconds ?? -1,
            isPrePlayPosterVisible,
          };
        }
      },
    }),
    [
      togglePlay,
      playerControlsRef,
      fullscreenRef,
      playerVideoRef,
      isPlaying,
      totalDurationSeconds,
      isPrePlayPosterVisible,
    ]
  );
}
